import React from 'react';
import { logError } from '@edx/frontend-platform/logging';

import type {
  AuthenticatedUser, AllowedCourseItem, CourseCatalogueItem, CourseGrade,
} from '../types';

interface PredefinedEnabledCourse {
  course_id: string;
  description: string;
  duration: string;
}

export function addArrowClickHandlers(): void {
  const coursesGrid = document.querySelector(
    '.course-container',
  ) as HTMLElement;
  let courseWidth: number;
  let currentPosition = 0;
  let totalItems: number;
  let rectangle: HTMLElement;
  let initialWidth: number;

  function calculateWidths(): void {
    const courseItem = document.querySelector(
      '.course-item-custom',
    ) as HTMLElement;
    courseWidth = (courseItem?.offsetWidth ?? 0) + 24; // added the gap width
    totalItems = coursesGrid.children.length;
    rectangle = document.querySelector(
      '.courses-slider-control .overlap-group-2 .rectangle-7',
    ) as HTMLElement;
    initialWidth = 100 / totalItems;
    rectangle.style.width = `${initialWidth}%`;
  }

  function updateRectangleWidth(): void {
    const currentPositionPercentage = (-currentPosition / (courseWidth * (totalItems - 1))) * 100;
    let newWidth = (initialWidth + currentPositionPercentage) * (100 / (100 + initialWidth));
    newWidth = Math.min(newWidth, 100);
    rectangle.style.width = `${newWidth.toFixed(2)}%`;
  }

  function updateButtonStates(): void {
    const previousButton = document.querySelector(
      '.previous_course',
    ) as HTMLButtonElement;
    const nextButton = document.querySelector(
      '.next_course',
    ) as HTMLButtonElement;

    if (currentPosition === 0) {
      previousButton.classList.add('disabled');
      previousButton.disabled = true;
    } else {
      previousButton.classList.remove('disabled');
      previousButton.disabled = false;
    }

    if (currentPosition <= -courseWidth * (totalItems - 1)) {
      nextButton.classList.add('disabled');
      nextButton.disabled = true;
    } else {
      nextButton.classList.remove('disabled');
      nextButton.disabled = false;
    }
  }

  calculateWidths();
  updateButtonStates();
  updateRectangleWidth();

  const nextButton = document.querySelector(
    '.next_course',
  ) as HTMLButtonElement;
  nextButton.addEventListener('click', () => {
    currentPosition -= courseWidth;
    coursesGrid.style.transform = `translateX(${currentPosition}px)`;
    updateButtonStates();
    updateRectangleWidth();
  });

  const previousButton = document.querySelector(
    '.previous_course',
  ) as HTMLButtonElement;
  previousButton.addEventListener('click', () => {
    currentPosition += courseWidth;
    coursesGrid.style.transform = `translateX(${currentPosition}px)`;
    updateButtonStates();
    updateRectangleWidth();
  });

  window.addEventListener('resize', () => {
    calculateWidths(); // Recalculate widths on window resize
    updateButtonStates();
    updateRectangleWidth();
  });
}

export const isCourseA5DayChallenge = (
  courseID: string,
  returnIndex: boolean,
  allowedCourses: AllowedCourseItem[],
) => {
  const conditionLabel = '5-day-challenge';
  const resultIndex = allowedCourses.findIndex(
    (pre) => pre?.type === conditionLabel && pre.course_id === courseID,
  );
  return returnIndex ? resultIndex : resultIndex !== -1;
};

const getPreviousNon5DayChallengeCourseID = (courseID: string, allowedCourses: AllowedCourseItem[]) => {
  const courseIndex = isCourseA5DayChallenge(courseID, true, allowedCourses);
  if (typeof courseIndex === 'number' && courseIndex > 0) {
    return allowedCourses[courseIndex - 1].course_id;
  }
  return null;
};

export const getNextCourseArrays = (
  course_id: string,
  enabledCourses: CourseCatalogueItem[],
  allowedCourses: AllowedCourseItem[],
): CourseCatalogueItem[] | PredefinedEnabledCourse[] => {
  const courseID = !isCourseA5DayChallenge(course_id, false, allowedCourses)
    ? course_id
    : getPreviousNon5DayChallengeCourseID(course_id, allowedCourses);
  const index = enabledCourses.findIndex(
    (course) => course.search?.data?.id === courseID,
  );
  if (index === -1 || index === enabledCourses.length - 1) {
    return [];
  }
  return enabledCourses.slice(index + 1);
};

export const isEligibleToEnrollV2 = (
  prerequisite: string | undefined,
  setLoaded: React.Dispatch<React.SetStateAction<boolean>> | null,
  returnCompletion: boolean,
  authenticatedUser: AuthenticatedUser | null,
  completion?: { earned: number, possible: number, percent: number },
  grade?: CourseGrade | null,
): Promise<boolean | { eligible: boolean; completed: boolean, progress: boolean }> => { // eslint-disable-line
  return new Promise((resolve) => {
    // eslint-disable-next-line
    setLoaded && setLoaded(false);

    if (!prerequisite) {
      // if not prerequisite set, I guess we should resolve to t
      // eslint-disable-next-line
      setLoaded && setLoaded(true);
      resolve(returnCompletion ? { eligible: true, completed: false, progress: false } : true);
      return;
    }

    if (authenticatedUser) {
      // eslint-disable-next-line
      setLoaded && setLoaded(true);

      if (!completion) {
        resolve(returnCompletion ? { eligible: false, completed: false, progress: false } : false);
      }

      try {
        const progressPercentage = Number(completion?.percent) * 100 || 0;
        const isInProgress = completion ? completion?.percent > 0 && completion?.percent < 1 : false;
        // Treat grade as passing when missing (ungraded course / no persistent grade yet).
        const gradeMissing = grade == null || grade.earned === null;
        const gradePassed = gradeMissing ? true : grade.passed === true;
        const isComplete = progressPercentage === 100 && gradePassed;
        if (returnCompletion) {
          resolve(
            isComplete
              ? { eligible: true, completed: true, progress: isInProgress }
              : { eligible: false, completed: false, progress: isInProgress },
          );
        } else {
          resolve(isComplete);
        }
      } catch (error: unknown) {
        logError('Getting course progress failed:', error);
        resolve(returnCompletion ? { eligible: false, completed: false, progress: false } : false);
      }
    } else {
      resolve(returnCompletion ? { eligible: false, completed: false, progress: false } : false);
    }
  });
};

export const getEnabledCourse = (
  coursesData: CourseCatalogueItem[],
  allowedCourses: AllowedCourseItem[],
  username?: string,
) => {
  let filteredCourses = coursesData;
  if (username) {
    const userCourses = allowedCourses.filter((course: AllowedCourseItem) => (
      course?.instructors?.includes(username)
    ));
    if (userCourses) {
      filteredCourses = coursesData.filter((courseInstance) => (
        userCourses.some((userCourse) => (
          userCourse.course_id === courseInstance?.search?.data?.id
        ))));
    }
  } else {
    filteredCourses = coursesData.filter((courseInstance) => (
      allowedCourses.some(
        (enabledCourse) => (
          enabledCourse.course_id === courseInstance?.search?.data?.id && !enabledCourse?.type
        ),
      )));
  }

  return filteredCourses.sort((a, b) => {
    const aIndex = allowedCourses.findIndex(
      (enabledCourse) => enabledCourse.course_id === a?.search?.data?.id,
    );
    const bIndex = allowedCourses.findIndex(
      (enabledCourse) => enabledCourse.course_id === b?.search?.data?.id,
    );
    return aIndex - bIndex;
  });
};
